<script lang="ts" setup>
import { ConditionElement, ConditionValue } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import _ from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { makeRangeLabel } from '@/filters/utils'
import { Gender } from '@/i18n'
import { useStore } from '@/store'
import { CriteriaList, isLocalizedOption } from '@/store/modules/filters/types.js'
import { ConditionGroup, CriterionCondition, ExistingCriterionConditionDto } from '@/store/modules/searches/types.js'
import { ModeEnum } from '@/store/modules/settings/types.js'

import { documentCategories, textualHealthDataCategories } from '../stay-displayer/health-data/documentLabels'
import filtersData from './FiltersData'

const { t } = useI18n()
const store = useStore()

const props = withDefaults(defineProps<{
  canRemoveCriteria?: boolean
  criteriaGroup: ConditionGroup
  editingCriterionId?: number | string | null
  internalOperator: string
  displayGroupedCriteria?: boolean  // group criteria with same type
  isGlobal?: boolean                // is global criteria
  maxCriteria?: number
  mode: string
}>(), {
  canRemoveCriteria: true,
  displayGroupedCriteria: false,
  editingCriterionId: null,
  isGlobal: false,
  maxCriteria: 0,
})

const emit = defineEmits<{
  (e: 'edit-criteria', condition: ExistingCriterionConditionDto): void
  (e: 'remove-criteria', condition: ExistingCriterionConditionDto): void
}>()

const textualReportCategories = { ...documentCategories, ...textualHealthDataCategories }

const healthConstantsList = computed(() => store.state.remoteLists['constants'])
const pmsiCountry = computed(() => store.state.settings.pmsi.country)

const criteriaList = computed(() => {
  const result: CriteriaList = {}
  _.forEach(props.criteriaGroup.criteriaList, (currentField: CriterionCondition) => {
    const criteriaType = currentField.type
    const currentValue = currentField.value

    if (currentValue) {
      const label = getCriteriaLabel(criteriaType)
      if (result[criteriaType]) {
        result[criteriaType].currentValues.push({
          id: currentField.id,
          rawContent: currentValue,
          content: renderCriteria(criteriaType, currentValue),
          type:criteriaType,
          modifiers: getModifiers(currentValue)
        })
      } else {
        result[criteriaType] = {
          label,
          currentValues: [{
            id: currentField.id,
            rawContent: currentValue,
            content: renderCriteria(criteriaType, currentValue),
            type:criteriaType,
            modifiers: getModifiers(currentValue)
          }]
        }
      }
    }
  })

  return result
})


function getCriteriaLabel(rawCriteriaType) {
  const splitCriteriaType = rawCriteriaType.split('__')
  const criteriaType = splitCriteriaType[0]
  if(splitCriteriaType.length > 1){
    return `${t(`filter.labels.${criteriaType}`)} (${textualReportCategories[splitCriteriaType[1]].toLowerCase()})`
  }
  return t(`filter.labels.${criteriaType}`)
}


function getConstantTitle(constantDescription: string) {
  const constant = healthConstantsList.value.find((c) => c.description === constantDescription)

  return constant ? constant.description : ''
}


function renderCriteria(rawType, value) {
  if (!value) {
    return ''
  }
  const type = rawType.indexOf('__') === -1 ? rawType : rawType.split('__')[0]

  switch (type) {
    case 'presentContent':
    case 'absentContent':
    // If there is a \x1F separator between the modifiers and the text, we show what's after the separator
    // If there is no \x1F, then the substring will start from 0, effectively doing nothing.
      return value.substring(value.indexOf('\x1F') + 1)

    case 'stayDuration':
      value = value.split('_')
      // We replace 0 by null, to avoid having a label like "0j to 2j" but rather "<2j"
      return makeRangeLabel(t, value[0], value[1], 'j', Gender.Feminine)

    case 'patientAge':
      value = value.split('_')
      return makeRangeLabel(t, value[0], value[1], ' ans')

    case 'startingDmsGap':
    case 'currentDmsGap':
      value = value.split('_')
      return makeRangeLabel(t, value[0], value[1], 'j')

    case 'healthConstant':
      value = value.split('\x1F')
      return `${getConstantTitle(value[0])} ${makeRangeLabel(t, value[1], value[2])}`

    case 'biologyResult':
      value = value.split('\x1F')
      if (value.length === 3) {
        return `${value[0]} ${makeRangeLabel(t, value[1], value[2])}`
      }

      return `${value[0]} à ${value[1]}`

    case 'compareDiagnosis':
    case 'gender':
    case 'hasSession':
    case 'hasTextualDocuments':
    case 'hasLabResults':
    case 'hasHealthConstants':
    case 'hasDrugEvents':
    case 'releaseMode':
    case 'admissionMode':
    case 'origin':
    case 'destination':
    case 'isPostSurgical':
      if (filtersData[rawType].optionFiltered && filtersData[rawType].options.every(isLocalizedOption)) {
        return filtersData[rawType].options.find((opt) => opt.country === pmsiCountry.value && opt.value === value).label
      }
      return filtersData[rawType]['labels'][value]

    case 'fromLastSurgeryDate':
    case 'fromStayDate':
      value = dayjs(value).format('L')

      return `À partir du ${value}`
    case 'toLastSurgeryDate':
    case 'toStayDate':
      value = dayjs(value).format('L')

      return `Jusqu'au ${value}`

    case 'presentDocumentCategory':
    case 'absentDocumentCategory':

      return documentCategories[value] ?? textualHealthDataCategories[value]

    case 'presentDepBehaviour':
    case 'presentDepCognitive':
    case 'presentDepContinence':
    case 'presentDepDressing':
    case 'presentDepFeeding':
    case 'presentDepMoving':
    case 'presentDepSocial':
    case 'presentDepPhysical':
    case 'absentDepBehaviour':
    case 'absentDepCognitive':
    case 'absentDepContinence':
    case 'absentDepDressing':
    case 'absentDepFeeding':
    case 'absentDepMoving':
    case 'absentDepSocial':
    case 'absentDepPhysical':
    case 'qaAbsentDepBehaviour':
    case 'qaAbsentDepCognitive':
    case 'qaAbsentDepContinence':
    case 'qaAbsentDepDressing':
    case 'qaAbsentDepFeeding':
    case 'qaAbsentDepMoving':
    case 'qaAbsentDepSocial':
    case 'qaAbsentDepPhysical':
      value = value.split('_')
      return makeRangeLabel(t, value[0], value[1], '', Gender.Feminine)

    case 'lastRhsRehabilitationActCount':
    case 'medicalActCount':
    case 'rehabilitationActCount':
    case 'rumCount':
      value = value.split('_')
      return makeRangeLabel(t, value[0], value[1])
    default:
      return value
  }
}

function getModifiers(value: string) {
  if (value.indexOf('\x1F') === -1) {
    return {}
  }

  const modifiers = {
    includeAntecedents: false,
    includeFamilyAntecedents: false,
    wordDistance: -1,
  }
  const strModif = value.substring(0, value.indexOf('\x1F'))
  const strModifSplit = strModif.split('_')

  strModifSplit.forEach((modif) => {
    if (modif === 'a') {
      modifiers.includeAntecedents = true
    } else if (modif === 'f') {
      modifiers.includeFamilyAntecedents = true
    } else if (modif === 'n') {
      modifiers.includeNegations = true
    } else if (modif.substring(0, 2) === 'wd') {
      modifiers.wordDistance = parseInt(modif.substring(2))
    }
  })

  return modifiers
}

const removeCriteria = (criterion: CriterionCondition): void =>
  emit('remove-criteria', { criteriaGroupId: props.criteriaGroup.id, criterion })

const editCriteria = (criterion: CriterionCondition): void =>
  emit('edit-criteria', { criteriaGroupId: props.criteriaGroup.id, criterion })

function selectDisplayedCriteria(values) {
  if (props.maxCriteria > 0) {
    values = values.slice(0, props.maxCriteria)
  }

  return values.map((criterion) => {
    if (['presentContent', 'absentContent'].indexOf(criterion.type) < 0) {
      return criterion
    }

    criterion.value = criterion.value || criterion.rawContent

    return {
      id: criterion.id,
      type: criterion.type,
      rawContent: criterion.value,
      content: renderCriteria(criterion.type, criterion.value),
      modifiers: getModifiers(criterion.value)
    }
  })
}
</script>

<template>
  <div name="CriteriaDisplayer">
    <div v-if="displayGroupedCriteria">
      <div
        v-for="(criteria, criteriaName) in criteriaList"
        :key="`criteria_list_${criteriaName}`"
        class="row g-0 align-items-center"
      >
        <div
          v-if="criteriaName === 'exclusiveMedicalAct'"
          class="col-12 mb-3 fst-italic text-secondary"
        >
          Les séjours doivent posséder uniquement des actes présents dans la liste d'actes exclusifs suivante :
        </div>
        <div class="col-5 px-2 criteria-label text-end">
          {{ criteria.label }}
        </div>
        <div class="col row g-0 px-0 py-1">
          <div v-if="criteria.currentValues">
            <div
              v-for="(value, idx) in selectDisplayedCriteria(criteria.currentValues)"
              :key="`criteria__${criteriaName}__${idx}`"
              class="col-auto mx-1 mb-1 ps-2 pe-2 badge text-wrap"
              :class="{
                'text-bg-highlight': isGlobal,
                'text-bg-primary': !isGlobal,
                'editing': value.id === editingCriterionId,
              }"
            >
              <condition-value
                :content="value.content"
                :modifiers="value.modifiers"
                :can-remove="canRemoveCriteria"
                :can-edit="mode !== ModeEnum.AUTOMATION"
                @remove="removeCriteria(value)"
                @edit="editCriteria(value)"
              />
            </div>
            <p v-if="maxCriteria > 0 && criteria.currentValues.length > maxCriteria">
              ...
            </p>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="(!criteriaGroup.criteriaList || criteriaGroup.criteriaList.length === 0)"
      class="text-center fst-italic text-secondary"
    >
      <small>Ce groupe ne contient aucun critère.</small>
    </div>
    <div
      v-else
      class="container"
    >
      <div
        v-for="(criterion, criterionIdx) in selectDisplayedCriteria(criteriaGroup.criteriaList)"
        :key="`criteria_list_${criterionIdx}`"
        class="text-center row"
      >
        <condition-element
          :label="getCriteriaLabel(criterion.type)"
          :operator="criterionIdx > 0 ? internalOperator : null"
          :value="renderCriteria(criterion.type, criterion.value)"
          :is-textual-value="['presentContent', 'absentContent'].includes(criterion.type)"
          :can-remove="canRemoveCriteria"
          :badge-class="{
            'text-bg-highlight': isGlobal,
            'text-bg-primary': !isGlobal,
            'editing': criterion.id === editingCriterionId,
          }"
          :can-edit="mode !== ModeEnum.AUTOMATION"
          @remove="removeCriteria(criterion)"
          @edit="editCriteria(criterion)"
        >
          <template #textual-value>
            <condition-value
              :content="criterion.content"
              :modifiers="criterion.modifiers"
              :can-remove="canRemoveCriteria"
              :can-edit="!isGlobal && mode !== ModeEnum.AUTOMATION"
              @remove="removeCriteria(criterion)"
              @edit="editCriteria(criterion)"
            />
          </template>
        </condition-element>
      </div>
      <p
        v-if="maxCriteria > 0 && criteriaGroup.criteriaList.length > maxCriteria"
        class="text-center"
      >
        ...
      </p>
    </div>
  </div>
</template>
